import React, { useState } from 'react';
import { Card, Button, Space, Modal, Typography } from 'antd';
import { CheckCircleOutlined, SwapOutlined } from '@ant-design/icons';
import EFluidType from '../../enums/EFluidType';

const { Text } = Typography;

/**
 * Seletor de fluido do PWA — mesmo padrão do MachineSelector:
 * na tela fica apenas um card compacto com o fluido escolhido (ocupa pouco
 * espaço) e as opções abrem em um MODAL com cards grandes touch-friendly,
 * um por fluido, cada um com sua cor.
 *
 * Compatível com o antd Form.Item (recebe `value`/`onChange` injetados), então
 * as `rules` de validação continuam funcionando sem mudança.
 *
 * Props:
 *  - value: código do fluido selecionado (ex.: 'SOLVENT').
 *  - onChange(code): chamado ao tocar em um card do modal.
 *  - options: lista { value, label } (padrão: EFluidType.asOptions()).
 *  - color: cor do fluxo (borda do card compacto e botão primário).
 */

// Cor por fluido — remete ao conteúdo (amarelo p/ solvente amarelo, marrom
// p/ borra, azul-água etc.), reforçando o reconhecimento visual na fábrica.
export const FLUID_COLORS = {
  SOLVENT: '#1677ff',        // azul (solvente limpo)
  BORRA: '#8a6d52',          // marrom (borra)
  WATER: '#13c2c2',          // ciano (água)
  YELLOW_SOLVENT: '#d97706', // âmbar (solvente amarelo)
  RC: '#722ed1',             // violeta (recarga)
};

const FluidCardSelect = ({ value, onChange, options = EFluidType.asOptions(), color = '#1b3663' }) => {
  const [open, setOpen] = useState(false);

  const selectedOpt = options.find((o) => o.value === value) || null;
  const selectedColor = FLUID_COLORS[value] || color;

  const handlePick = (code) => {
    if (onChange) onChange(code);
    setOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {selectedOpt ? (
        <Card
          size="small"
          styles={{ body: { padding: 12 } }}
          style={{ borderColor: selectedColor, borderWidth: 1, background: '#fff' }}
          aria-label={`Fluido selecionado: ${selectedOpt.label}`}
        >
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <CheckCircleOutlined style={{ color: selectedColor }} />
              <Text strong style={{ fontSize: 16, color: selectedColor }}>{selectedOpt.label}</Text>
            </Space>
            <Button
              icon={<SwapOutlined />}
              onClick={() => setOpen(true)}
              aria-label="Trocar o fluido"
            >
              Trocar
            </Button>
          </Space>
        </Card>
      ) : (
        <Button
          size="large"
          block
          onClick={() => setOpen(true)}
          aria-label="Escolher o fluido"
          style={{ borderColor: color, color }}
        >
          Escolher o fluido
        </Button>
      )}

      {/* Modal com as opções em cards grandes (um por fluido, na cor dele). */}
      <Modal
        title="Escolher fluido"
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        centered
      >
        <div
          role="radiogroup"
          aria-label="Tipo de fluido"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 12,
          }}
        >
          {options.map((opt) => {
            const optColor = FLUID_COLORS[opt.value] || color;
            const selected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`Fluido ${opt.label}`}
                onClick={() => handlePick(opt.value)}
                style={{
                  minHeight: 96,
                  border: selected ? '3px solid #fff' : 'none',
                  outline: selected ? `3px solid ${optColor}` : 'none',
                  borderRadius: 14,
                  background: optColor,
                  color: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '12px 10px',
                  fontSize: 16,
                  fontWeight: 700,
                  lineHeight: 1.2,
                  textAlign: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.16)',
                  transition: 'transform 0.08s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
                onTouchStart={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                onTouchEnd={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                {opt.label}
                {selected && (
                  <span style={{ fontSize: 12, fontWeight: 600 }}>
                    <CheckCircleOutlined /> Selecionado
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
};

export default FluidCardSelect;
